import React, {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {Seo} from '../../components/seo'
import {Layout} from '../../components/layout'
import {Box, Container, Flex, Stack, Text, BaseButton} from '../../vanilla'
import {Breadcrumbs} from '../../components/breadcrumbs'
import {SearchStoreBox} from './partials/search-store-box'
import {SubmitHandler, UseFormReturn, useForm} from 'react-hook-form'
import {ChevronLeftIcon} from '../../components/icons'
import {useHistory} from 'react-router'
import {searchStoreBoxWrapper, storeListSearchBoxColumn, storeListMapBoxColumn} from './styles.css'
import {StoreDetailItem} from './partials/store-item'
import {GoogleMapsGeocoder} from '../../services/googleMapsGeocoder'
import {observer} from 'mobx-react-lite'
import {useGlobalStore, useStoreDetailsStore} from '../../store/hooks/useStore'
import {useMyLocation} from '../../hooks/use-my-location'
import {Wrapper} from '@googlemaps/react-wrapper'
import {useSearchParams} from '../../hooks/use-search-params'
import {StoreDetailPage} from './partials/store-detail-page'
import {StoreLocatorMap} from './partials/store-locator-map'
import { usePageDataTracking } from '../../analytics/page-tracking'
import { StoreFinderSearchBarFormFields } from '../../types/forms'
import ViewAllStores from './partials/view-all-stores'

const StoreFinder = () => {
  const params = useSearchParams()
  const location = useLocation()
  const isStoreDetailPage = params.get('StoreID') !== null
  const isAllStoresPage = location.pathname === '/store-finder/all-stores'

  return (
    <Layout>
      {isStoreDetailPage ? <StoreDetailPage /> : isAllStoresPage ? <ViewAllStores /> : <StoreLocator />}
    </Layout>
  )}

const StoreLocator = observer(() => {
  const history = useHistory()
  const {googleMapsAPIkey} = useGlobalStore()
  const {currentSearch, findStoresByCoords} = useStoreDetailsStore()
  const searchParams = useSearchParams()

  const prevSearch = searchParams?.get('prevSearch')

  const form: UseFormReturn<StoreFinderSearchBarFormFields> = useForm({mode: 'onBlur', shouldUnregister: false})
  const [geocoderService] = useState(() => new GoogleMapsGeocoder({API_KEY: googleMapsAPIkey}))
  const {getUserCurrentCoords} = useMyLocation()

  /* Controls page layout type, initially we are on Store Locator page, where only one search form.
   * Toggling this state we are changing layout to store list with map. */
  const [landingLayout, setLandingLayout] = useState(true)

  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null)
  const [selectedCoordinates, setSelectedCoordinates] = useState<{lat: number; lng: number} | null>(
    null
  )

  const handleUseMyLocationClick = async () => {
    const coords = await getUserCurrentCoords()

    if (coords) {
      setSelectedCoordinates(coords)
      await findStoresByCoords(coords)

      if (landingLayout) {
        setLandingLayout(false)
      }
    }
  }

  const onSubmitSearchForm: SubmitHandler<StoreFinderSearchBarFormFields> = async (data) => {
    const coords = await geocoderService.getAddressCoordinates(data.storeFinder)

    if (coords) {
      setSelectedCoordinates(coords)
      await findStoresByCoords(coords)
    }

    if (landingLayout) {
      setLandingLayout(false)
    }
  }

  // Once getting store details, setting selected id to 1st in the list
  // Highlight store's marker and expand detail on mobile
  useEffect(() => {
    if (currentSearch.length) {
      setSelectedStoreId(currentSearch[0].id)
    }
  }, [currentSearch])

  // Handles back state from Store Detail page
  useEffect(() => {
    if (prevSearch) {
      form.setValue('storeFinder', decodeURIComponent(prevSearch))
      form.handleSubmit(onSubmitSearchForm)()
    }
  }, [prevSearch])
  
  const sendPageData = usePageDataTracking()
  const pageType = 'storelocator'
  const pageCategory = 'Store Locator'

  useEffect(() => {
    sendPageData(pageType, pageCategory)
  }, [])

  return (
    <Wrapper apiKey={googleMapsAPIkey}>
      <Seo title="Iceland Store Locator | Find Stores Near You" />
      <Container
        paddingTop={['0px', '12px']}
        paddingX={['0px', '16px']}
        paddingBottom={['24px', '36px']}
      >
        <Stack width="full" spacing={['0px', '12px']}>
          <Breadcrumbs
            display={['none', 'block']}
            breadcrumbs={[{id: 'STORE_LOCATOR', name: 'Store Locator'}]}
          />
          <Flex
            display={['flex', 'none']}
            bg="white"
            gap="4px"
            align="center"
            paddingX="16px"
            paddingY="12px"
            borderBottom="1px"
            borderColor="gray200"
            onClick={() => history.goBack()}
          >
            <ChevronLeftIcon style={{width: 14, height: 14}} color="gray800" />
            <Text variant="text1" lineHeight="none">
              Back
            </Text>
          </Flex>
          <Box display={['block', 'none']} paddingTop="20px" paddingX="16px" bg="white">
            <Text
              as="h3"
              variant="unstyled"
              color="gray800"
              align="left"
              lineHeight="shorter"
              fontWeight="bold"
              style={{fontSize: 26}}
            >
              StoreFinder
            </Text>
          </Box>
          {landingLayout ? (
            <Box className={searchStoreBoxWrapper} marginX="auto" width="full" marginBottom="52px">
              <SearchStoreBox
                landingLayout
                form={form}
                onSubmit={onSubmitSearchForm}
                handleUseMyLocationClick={handleUseMyLocationClick}
              />
            </Box>
          ) : (
            <Stack width="full" spacing="20px">
              <Flex
                align={['stretch', 'flex-start']}
                direction={['column', 'row']}
                width="full"
                gap="8px"
              >
                <Stack spacing="8px" className={storeListSearchBoxColumn} align="flex-start">
                  <Box display={['none', 'flex']}>
                    <BaseButton
                      variant="unstyled"
                      alignItems="center"
                      gap="12px"
                      paddingX="28px"
                      paddingY="24px"
                      bg="white"
                      height="auto"
                      onClick={() => {
                        setLandingLayout(true)
                      }}
                    >
                      <ChevronLeftIcon boxSize="24px" color="gray800" />
                      <Text variant="heading1" lineHeight="shorter">
                        Back
                      </Text>
                    </BaseButton>
                  </Box>
                  <SearchStoreBox
                    landingLayout={false}
                    form={form}
                    onSubmit={onSubmitSearchForm}
                    handleUseMyLocationClick={handleUseMyLocationClick}
                  />
                </Stack>
                <Box width="full" className={storeListMapBoxColumn} bg="gray100">
                  <StoreLocatorMap
                    selectedStoreId={selectedStoreId || ''}
                    setSelectedStoreId={setSelectedStoreId}
                    mapOptions={{
                      center: {
                        lng: selectedCoordinates?.lng ?? 0,
                        lat: selectedCoordinates?.lat ?? 0,
                      },
                      zoom: 7,
                    }}
                  />
                </Box>
              </Flex>
              <Stack spacing={['0px', '20px']}>
                {!currentSearch?.length ? (
                  <Box paddingX="36px" paddingY="16px" bg="white">
                    <Text variant="text3" lineHeight="shorter">
                      Sorry, there are no stores in your area.
                    </Text>
                  </Box>
                ) : (
                  currentSearch.map((store, idx) => (
                    <StoreDetailItem
                      key={store.id}
                      num={idx + 1}
                      store={store}
                      selectedStoreId={selectedStoreId || ''}
                      handleMarkerClick={setSelectedStoreId}
                      searchVal={form.getValues('storeFinder')}
                    />
                  ))
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Container>
    </Wrapper>
  )
})

export default StoreFinder
